import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { MouseEvent } from "react";
import { Handle, NodeProps, NodeToolbar, Position, NodeResizer } from "reactflow";
import { useAppSelector } from "../../hooks";
import {
  selectBlockIOPSSummary,
  selectDragHandleNode,
  selectOutputBlockTypes,
  selectRootPercentageSuccess,
} from "../../store/game/selectors";
import { removeSelectedBlock } from "../../store/game/gameSlice";
import { ROOT_BLOCK_ID, blockConfigs } from "../../models/block";
import CardContent from "@mui/material/CardContent";
import { BlockImage } from "./BlockImage";
import { IopsSummary } from "./IopsSummary";
import Fab from "@mui/material/Fab";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { BlockTypes } from "../../models/block-types";
import { Card } from "@mui/material";
import { useDispatch } from "react-redux";
import { BlockInfoDisplay } from "./BlockInfoDisplay";
import TextField from "@mui/material/TextField";
import { renameBlock, openBlockSettings, updateBlockDynamicProperties, setDraggingNode } from "../../store/game/gameSlice";
import SettingsIcon from '@mui/icons-material/Settings';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BlockInfoModal } from "./BlockInfoModal";

export type ReactFlowBlockProps = {
  label: string;
  blockType: BlockTypes;
};

//A function that returns a boolean indicating if the current block can connect to the connectingNodeId
const canConnect = (
  connectingNodeId: string,
  connectingNodeType: BlockTypes,
  connectingNodeHandleType: string,
  currentNodeId: string,
  currentNodeType: BlockTypes,
  outputTypes: Map<string, number>,
  connectingOutputTypes: Map<string, number>
) => {
  if (connectingNodeId === currentNodeId) return false;
  if (
    currentNodeType === BlockTypes.ARROW ||
    connectingNodeType === BlockTypes.ARROW ||
    currentNodeType === BlockTypes.TEXT_BOX ||
    connectingNodeType === BlockTypes.TEXT_BOX
  )
    return false;
  if (!currentNodeType || !blockConfigs[currentNodeType]) return false;
  if (connectingNodeHandleType === "source") {
    //connecting node is upstream
    const targetNodeAllowedTypes =
      blockConfigs[currentNodeType].allowedInputTypes;
    if (
      !targetNodeAllowedTypes ||
      !connectingNodeType ||
      !targetNodeAllowedTypes.has(connectingNodeType)
    ) {
      return false;
    }
    if (
      maxConnectionsReached(
        connectingOutputTypes,
        currentNodeType,
        connectingNodeType
      )
    )
      return false;
  } else {
    //connecting node is downstream
    const sourceNodeAllowedTypes =
      blockConfigs[currentNodeType].allowedOutputTypes;
    if (
      !sourceNodeAllowedTypes ||
      !connectingNodeType ||
      !sourceNodeAllowedTypes.has(connectingNodeType)
    ) {
      return false;
    }
    if (maxConnectionsReached(outputTypes, connectingNodeType, currentNodeType))
      return false;
  }
  return true;
};

const getBackgroundColor = (
  connectionIsHappening: boolean,
  enableHandles: boolean,
  currentNodeID: string,
  connectingNodeID: string | undefined
) => {
  if (!connectionIsHappening) return "white";
  if (connectingNodeID === currentNodeID) return "white";
  return enableHandles ? "green" : "red";
};

//a function that returns if the max connections for a block has been reached
const maxConnectionsReached = (
  outputConnectionCounts: Map<string, number>,
  connectingBlockType: BlockTypes,
  targetBlockType: BlockTypes
) => {
  if (!targetBlockType || !blockConfigs[targetBlockType]) return false;
  const connectorLimits = blockConfigs[targetBlockType].outputConnectorLimits;
  if (!connectorLimits) return false;
  if (!connectingBlockType || !outputConnectionCounts.has(connectingBlockType)) return false;
  const result =
    (outputConnectionCounts.get(connectingBlockType) || 0) >=
    connectorLimits[connectingBlockType];
  return result;
};

const ReactFlowBlock: React.FC<NodeProps<ReactFlowBlockProps>> = ({
  selected,
  data,
  id,
}) => {
  const dispatch = useDispatch();

  const [isEditingName, setIsEditingName] = React.useState(false);
  const [draftName, setDraftName] = React.useState("");
  const [isInfoOpen, setIsInfoOpen] = React.useState(false);

  const connectingNode = useAppSelector((state) =>
    selectDragHandleNode(state.game)
  );
  const rootPercentageSuccess = useAppSelector((state) =>
    selectRootPercentageSuccess(state.game)
  );
  const isStarted = useAppSelector((state) => state.game.isStarted);
  const iopsSummary = useAppSelector((state) =>
    selectBlockIOPSSummary(state.game.blockIOPS, id)
  );
  const blockOutputBlockTypes = useAppSelector((state) =>
    selectOutputBlockTypes(state.game, id)
  );
  const connectingBlockOutputBlockTypes = useAppSelector((state) =>
    selectOutputBlockTypes(state.game, connectingNode?.nodeID)
  );
  const block = useAppSelector((state) => state.game.blocks[id]);
  const currentInputIOPS = useAppSelector(
    (state) => state.game.blockIOPS?.[id]?.inputIOPS
  );
  const currentOutputIOPS = useAppSelector(
    (state) => state.game.blockIOPS?.[id]?.outputIOPS
  );

  const connectionIsHappening = !!connectingNode;
  const enableHandles =
    !connectionIsHappening ||
    canConnect(
      connectingNode?.nodeID,
      connectingNode.blockType,
      connectingNode.type,
      id,
      data.blockType,
      blockOutputBlockTypes,
      connectingBlockOutputBlockTypes
    );

  const bgColor = getBackgroundColor(
    connectionIsHappening,
    enableHandles,
    id,
    connectingNode?.nodeID
  );

  const handleColor = connectionIsHappening ? "blue" : "#B9BBB9";

  const showInputConnector =
    enableHandles &&
    data?.blockType &&
    data?.blockType !== BlockTypes.ARROW &&
    data?.blockType !== BlockTypes.TEXT_BOX &&
    blockConfigs[data.blockType] &&
    blockConfigs[data.blockType].allowedInputTypes &&
    blockConfigs[data.blockType].allowedInputTypes.size > 0;
  const showOutputConnector =
    enableHandles &&
    data?.blockType &&
    data?.blockType !== BlockTypes.ARROW &&
    data?.blockType !== BlockTypes.TEXT_BOX &&
    blockConfigs[data.blockType] &&
    blockConfigs[data.blockType].allowedOutputTypes &&
    blockConfigs[data.blockType].allowedOutputTypes.size > 0;

  const canBeDeleted = id !== ROOT_BLOCK_ID;

  const width = block?.width ?? 100;
  const height = block?.height ?? 100;
  const containerWidth = width;
  const containerHeight = height;

  const handleBlockDelete = (
    e: MouseEvent<HTMLButtonElement>,
    blockID: string
  ) => {
    e.stopPropagation();
    dispatch(removeSelectedBlock(blockID));
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canBeDeleted) {
      setIsEditingName(true);
      setDraftName(data?.label || (data?.blockType && blockConfigs[data.blockType]?.displayName) || "");
    }
  };

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(openBlockSettings(id));
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canBeDeleted) { // Avoid renaming ROOT
      setIsEditingName(true);
      setDraftName(data?.label || (data?.blockType && blockConfigs[data.blockType]?.displayName) || "");
    }
  };

  const handleNameSave = () => {
    setIsEditingName(false);
    if (draftName.trim() && draftName !== data.label) {
      dispatch(renameBlock({ blockId: id, name: draftName.trim() }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const isArrow = data.blockType === BlockTypes.ARROW;
  const isTextBox = data.blockType === BlockTypes.TEXT_BOX;
  const direction = block?.dynamicProperties?.direction || "right";
  let rotateDegree = block?.dynamicProperties?.rotation ?? 0;

  if (block?.dynamicProperties?.rotation === undefined) {
    if (direction === "up") rotateDegree = 270;
    if (direction === "down") rotateDegree = 90;
    if (direction === "left") rotateDegree = 180;
    if (direction === "right") rotateDegree = 0;
  }

  const finalBgColor = isTextBox ? "#ffffff" : (isArrow ? "transparent" : bgColor);
  const finalBorder = isTextBox ? "1px solid #000000" : (isArrow ? (selected ? "1px dashed #000000" : "none") : (selected ? "solid pink 2px" : "none"));
  const finalBoxShadow = (isArrow || isTextBox) ? "none" : undefined;

  const handleResizeStart = () => {
    dispatch(setDraggingNode(true));
  };

  const handleResizeEnd = () => {
    dispatch(setDraggingNode(false));
  };

  const handleRotateStart = (e: React.MouseEvent) => {
    dispatch(setDraggingNode(true));
    e.stopPropagation();
    e.preventDefault();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
      e.nativeEvent.stopPropagation();
    }

    const nodeElement = e.currentTarget.closest('.react-flow__node');
    if (!nodeElement) return;

    const rect = nodeElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
      const angleRad = Math.atan2(moveEvent.clientY - centerY, moveEvent.clientX - centerX);
      let angleDeg = angleRad * (180 / Math.PI);

      // Since the drag handle is at the right (0 degrees), the rotation is angleDeg
      let rotation = Math.round(angleDeg);
      if (rotation < 0) rotation += 360;
      rotation = rotation % 360;

      dispatch(
        updateBlockDynamicProperties({
          blockId: id,
          properties: { rotation }
        })
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      dispatch(setDraggingNode(false));
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <Box
      data-testid={`block-${data.blockType}`}
      sx={{
        display: "flex",
        flexDirection: "row",
        transform: isArrow ? `rotate(${rotateDegree}deg)` : undefined,
        transformOrigin: "center center",
        transition: isArrow ? "transform 0.05s ease-out" : undefined,
      }}
    >
      <NodeToolbar isVisible={selected} position={Position.Left}>
        <Box
          style={{
            width: `${40}px`,
            padding: "0px 5px",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
          }}
        >
          {canBeDeleted && (
            <>
              {!isArrow && !isTextBox && (
                <>
                  <Fab
                    color="primary"
                    aria-label="edit"
                    size="small"
                    style={{ transform: "scale(0.7)" }}
                    onClick={handleEditClick}
                  >
                    <EditIcon />
                  </Fab>
                  {data?.blockType && blockConfigs[data.blockType]?.hasSettings && (
                    <Fab
                      color="secondary"
                      aria-label="settings"
                      size="small"
                      style={{ transform: "scale(0.7)" }}
                      onClick={handleSettingsClick}
                    >
                      <SettingsIcon />
                    </Fab>
                  )}
                  <Fab
                    color="info"
                    aria-label="info"
                    size="small"
                    style={{ transform: "scale(0.7)" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsInfoOpen(true);
                    }}
                  >
                    <InfoOutlinedIcon />
                  </Fab>
                </>
              )}
              <Fab
                color="primary"
                aria-label="delete"
                size="small"
                style={{ transform: "scale(0.7)" }}
                onClick={(e) => {
                  handleBlockDelete(e, id);
                }}
              >
                <DeleteOutlineIcon />
              </Fab>

            </>
          )}
          {!canBeDeleted && (
            <Fab
              color="info"
              aria-label="info"
              size="small"
              style={{ transform: "scale(0.7)" }}
              onClick={(e) => {
                e.stopPropagation();
                setIsInfoOpen(true);
              }}
            >
              <InfoOutlinedIcon />
            </Fab>
          )}
        </Box>
      </NodeToolbar>
      {isArrow && (
        <NodeResizer
          color="#000000"
          minWidth={50}
          minHeight={20}
          isVisible={selected}
          onResizeStart={handleResizeStart}
          onResizeEnd={handleResizeEnd}
        />
      )}
      {isTextBox && (
        <NodeResizer
          color="#000000"
          minWidth={100}
          minHeight={40}
          isVisible={selected}
          onResizeStart={handleResizeStart}
          onResizeEnd={handleResizeEnd}
        />
      )}
      <Box>
        <Handle
          type="target"
          data-testid={`target-${data.blockType}`}
          position={Position.Top}
          isConnectable={showInputConnector}
          style={{
            visibility: showInputConnector ? "visible" : "hidden",
            left: 50,
            width: 20,
            height: 20,
            top: -10,
            backgroundColor: handleColor,
          }}
        />
        <Box
          style={{
            width: `${containerWidth}px`,
            height: `${containerHeight}px`,
            backgroundColor: " rgba(255, 255, 255, 0)",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Card
            className="block"
            style={{
              width: `${width}px`,
              height: `${height}px`,
              border: finalBorder,
              boxShadow: finalBoxShadow,
              overflow: "visible",
              backgroundColor: finalBgColor,
              boxSizing: "border-box",
            }}
          >
            <CardContent
              style={{
                padding: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflow: "visible",
                justifyContent: "center",
              }}
            >
              {isArrow ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  {/* Rotate Handle */}
                  {selected && (
                    <Box
                      className="nodrag"
                      onMouseDown={handleRotateStart}
                      sx={{
                        position: "absolute",
                        top: "calc(50% - 10px)",
                        right: "-30px",
                        left: "auto",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#000000",
                        border: "2px solid white",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        cursor: "grab",
                        zIndex: 1000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:active": { cursor: "grabbing" },
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          top: "9px",
                          left: "-12px",
                          width: "12px",
                          height: "2px",
                          backgroundColor: "#000000"
                        }
                      }}
                    >
                      <svg viewBox="0 0 24 24" style={{ width: "12px", height: "12px", fill: "none", stroke: "white", strokeWidth: 2.5, strokeLinecap: "round", strokeLinejoin: "round" }}>
                        <path d="M23 4v6h-6" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                      </svg>
                    </Box>
                  )}

                  {/* Arrow Wrapper rotated */}
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      px: "4px",
                      boxSizing: "border-box"
                    }}
                  >
                    <Box sx={{ height: "4px", backgroundColor: "#000000", flexGrow: 1, borderRadius: "2px" }} />
                    <svg viewBox="0 0 24 24" preserveAspectRatio="none" style={{ width: "32px", height: "16px", fill: "#000000", flexShrink: 0, marginLeft: "-2px" }}>
                      <path d="M6 4 L18 12 L6 20 Z" />
                    </svg>
                  </Box>
                </Box>
              ) : isTextBox ? (
                <Box
                  onDoubleClick={handleDoubleClick}
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                    boxSizing: "border-box"
                  }}
                >
                  {!isEditingName ? (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#4b5563",
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "pre-wrap",
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        userSelect: "none"
                      }}
                    >
                      {data?.label || "Double-click to type..."}
                    </Typography>
                  ) : (
                    <TextField
                      autoFocus
                      multiline
                      fullWidth
                      size="small"
                      variant="standard"
                      value={draftName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftName(e.target.value)}
                      onBlur={handleNameSave}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setIsEditingName(false);
                        }
                      }}
                      sx={{
                        width: "100%",
                        height: "100%",
                        "& .MuiInputBase-root": {
                          height: "100%",
                          fontSize: "12px",
                          alignItems: "center",
                        }
                      }}
                      inputProps={{
                        style: {
                          textAlign: "center",
                          padding: 0,
                        }
                      }}
                      InputProps={{ disableUnderline: true }}
                    />
                  )}
                </Box>
              ) : (
                <Box onDoubleClick={handleDoubleClick} sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <BlockImage
                    width={60}
                    height={60}
                    blockType={data?.blockType}
                    percentageSuccess={rootPercentageSuccess}
                  />
                  {!isEditingName ? (
                    <Box
                      sx={{
                        fontSize: "9px",
                        fontWeight: 600,
                        textAlign: "center",
                        lineHeight: 1.2,
                        color: "#333",
                        mt: "4px",
                        px: "2px",
                        wordBreak: "break-word",
                        width: "100%",
                      }}
                    >
                      {data?.label || (data?.blockType && blockConfigs[data.blockType]?.displayName) || ""}
                    </Box>
                  ) : (
                    <TextField
                      autoFocus
                      size="small"
                      variant="standard"
                      value={draftName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftName(e.target.value)}
                      onBlur={handleNameSave}
                      onKeyDown={handleKeyDown}
                      sx={{ width: "90%", mt: "2px" }}
                      inputProps={{
                        style: {
                          fontSize: "9px",
                          fontWeight: 600,
                          textAlign: "center",
                          padding: 0,
                        }
                      }}
                      InputProps={{ disableUnderline: true }}
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
        <Handle
          type="source"
          data-testid={`source-${data.blockType}`}
          position={Position.Bottom}
          id="b"
          isConnectable={showOutputConnector}
          style={{
            visibility: showOutputConnector ? "visible" : "hidden",
            left: 50,
            width: 20,
            height: 20,
            top: height - 10,
            backgroundColor: handleColor,
          }}
        ></Handle>
      </Box>
      {isStarted && !isArrow && !isTextBox && (
        <Box
          style={{
            height: `${containerHeight}px`,
            width: "auto",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IopsSummary summary={iopsSummary} gameIsStarted={isStarted} />
          </Box>
          {selected && block && (
            <BlockInfoDisplay block={block} title={(data?.blockType && blockConfigs[data.blockType]?.displayName) || ""} currentInputIOPS={currentInputIOPS} currentOutputIOPS={currentOutputIOPS} />
          )}
        </Box>
      )}
      <BlockInfoModal
        blockType={data.blockType}
        open={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />
    </Box>
  );
};

export default ReactFlowBlock;
